/**
 * @file CarouselV2 component
 *
 * A simple carousel component based on Swiper.
 */

import SelectorEngine from '../../utils/dom/selector-engine';
import Manipulator from '../../utils/dom/manipulator';
import EventHandler from '../../utils/dom/event-handler';
import BaseComponent from '../BaseComponent';

// Import Swiper and required modules
import Swiper from 'swiper';
import { 
	A11y,
	Autoplay,
	Controller,
	Keyboard,
	Navigation,
	Pagination,
	Parallax,
	EffectFade,
	Thumbs,
} from 'swiper/modules';
// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const NAME = 'CarouselV2';

const DATA_KEY = 'lta.carouselv2';
const EVENT_KEY = `.${DATA_KEY}`;
const DATA_API_KEY = '.data-api';

const EVENT_CLICK_DATA_API = `click${EVENT_KEY}${DATA_API_KEY}`;
const SELECTOR_DATA_TOGGLE = '[data-lta-carouselv2]';

const HIDDEN_CLASS = 'u-hidden';
const VISIBLE = 'visible';
const HIDDEN = 'hidden';

class CarouselV2 extends BaseComponent {
	// eslint-disable-next-line unicorn/no-null
	constructor(element = null, settings = {}) {
		super(element);

		const defaults = {
			slidesPerView: 1,
			slidesPerViewMd: 2,
			slidesPerViewXl: 4,
			spaceBetween: 16,
			spaceBetweenMd: 16,
			spaceBetweenXl: 24,
			swiperClass: '.swiper',
			showPagination: true,
			isThumbnailCarousel: false,
			loop: false,
			autoplay: false,
		};

		this.settings = {
			...defaults,
			...settings
		};

		this.componentWrapper_ = element;
		this.swiperInstance = undefined;
		
		if (this.componentWrapper_) {
			const instanceSettings = Manipulator.getDataAttributes(element);
			const hideCarouselControls = instanceSettings.carouselHideControls || false;
			const previousButton = SelectorEngine.findOne('.lta-carouselv2__prev', element);
			const nextButton = SelectorEngine.findOne('.lta-carouselv2__next', element);
			const isMobile = window.innerWidth < 768;

			if (hideCarouselControls) {
				if (isMobile) {
					previousButton.classList.add(HIDDEN_CLASS);
				} else {
					previousButton.style.visibility = HIDDEN;
				}
			}

			this.initCarouselV2Settings(instanceSettings);
			this.initCarouselV2(
				SelectorEngine.findOne(this.settings.swiperClass, this.componentWrapper_),
				hideCarouselControls,
				previousButton,
				nextButton
			);

			// import('swiper')
			// 	.then(({ Swiper, Navigation, Pagination, A11y, Keyboard, Thumbs }) => {
			// 		this.Swiper = Swiper;
			// 		this.Accessibility = A11y;
			// 		this.KeyboardControl = Keyboard;
			// 		this.Navigation = Navigation;
			// 		this.Pagination = Pagination;
			// 		this.Thumbs = Thumbs;

			// 		this.initCarouselV2Settings(instanceSettings);
			// 		this.initCarouselV2(
			// 			SelectorEngine.findOne(this.settings.swiperClass, this.componentWrapper_),
			// 			hideCarouselControls,
			// 			previousButton,
			// 			nextButton
			// 		);

			// 		return Swiper;
			// 	})
			// 	.catch((error) => {
			// 		// eslint-disable-next-line no-console
			// 		console.error(error);
			// 	});
		}
	}

	static get NAME() {
		return NAME;
	}

	initCarouselV2Settings(instanceSettings) {
		if (instanceSettings.slidesPerView !== undefined) this.settings.slidesPerView = instanceSettings.slidesPerView;
		if (instanceSettings.slidesPerViewMd !== undefined) this.settings.slidesPerViewMd = instanceSettings.slidesPerViewMd;
		if (instanceSettings.slidesPerViewXl !== undefined) this.settings.slidesPerViewXl = instanceSettings.slidesPerViewXl;

		if (instanceSettings.spaceBetween !== undefined) this.settings.spaceBetween = instanceSettings.spaceBetween;
		if (instanceSettings.spaceBetweenMd !== undefined) this.settings.spaceBetweenMd = instanceSettings.spaceBetweenMd;
		if (instanceSettings.spaceBetweenXl !== undefined) this.settings.spaceBetweenXl = instanceSettings.spaceBetweenXl;
	}

	initCarouselV2(swiperElement, hideCarouselControls, previousButton, nextButton) {
		this.Swiper = Swiper;
					this.Accessibility = A11y;
					this.KeyboardControl = Keyboard;
					this.Navigation = Navigation;
					this.Pagination = Pagination;
		const count = swiperElement.querySelector('.swiper-wrapper').children.length;
		let config = {
			modules: [
				A11y,
				Autoplay,
				Controller,
				Keyboard,
				Navigation,
				Pagination,
				Thumbs,
				Parallax,
				EffectFade,
			],
			slidesPerView: this.settings.slidesPerView,
			spaceBetween: this.settings.spaceBetween,
			breakpoints: {
				768: {
					slidesPerView: this.settings.slidesPerViewMd,
					spaceBetween: this.settings.spaceBetweenMd,
				},
				1200: {
					slidesPerView: this.settings.slidesPerViewXl,
					spaceBetween: this.settings.spaceBetweenXl,
				}
			},
			watchSlidesProgress: true,
			grabCursor: true,
			observer: true,
			autoplay: this.settings.autoplay,
			loop: this.settings.loop,
			a11y: {
				enabled: true
			},
			keyboard: {
				enabled: true,
				onlyInViewport: false
			},
			effect: this.settings.effect,
			fadeEffect: this.settings.fadeEffect,
		}

		if(this.settings.thumbnailCarousel) {
			config.thumbs = {
				swiper: this.settings.thumbnailCarousel,
			}
		}

		if(this.settings.showPagination) {
			SelectorEngine.findOne('.lta-carouselv2__controls', this._element)?.classList.remove(HIDDEN_CLASS);
			config.navigation = {
				prevEl: '.lta-carouselv2__prev',
				nextEl: '.lta-carouselv2__next'
			}
			config.pagination = {
				el: '.lta-carouselv2__pagination',
				clickable: true,
				renderBullet(index, className) {
					return `<button class="${className} ${className}${index}" aria-label="Slide ${index+1} of ${count}"></button>`;
				}
			}
		} else {
			SelectorEngine.findOne('.lta-carouselv2__controls', this._element)?.classList.add(HIDDEN_CLASS);
		}

		if(this.settings.isThumbnailCarousel) {
			config = {
				modules: [
					Autoplay,
					Controller,
					Keyboard,
					Navigation,
					Pagination,
					Thumbs,
				],
				slidesPerView: "auto",
				autoScrollOffset: 0,
				spaceBetween: 14,
				loopedSlides: 5,
				freeMode: this.settings.freeMode ?? true,
				allowTouchMove: true,
			}
			console.log({ config });
		}
		
		this.swiperInstance = new Swiper(swiperElement, config);
	}

	update() {
		this.swiperInstance?.update();
	}

	destroy() {
		this.swiperInstance.destroy(true, true);
	}
}

/**
 * Data Api implementation
 */
EventHandler.on(document, EVENT_CLICK_DATA_API, SELECTOR_DATA_TOGGLE, function (event) {
	event.preventDefault();

	CarouselV2.getInstance(this) || new CarouselV2(this);
});

export default CarouselV2;
